import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';

const ContactSection = styled.section`
  padding: 7.2rem 5%;
  background-color: ${props => props.theme.colors.background};
  position: relative;
`;

const ContactContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3.6rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div``;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  margin-bottom: 1.35rem;
  position: relative;
  color: ${props => props.theme.colors.text};
  
  &::after {
    content: '';
    display: block;
    width: 72px;
    height: 3.6px;
    background: ${props => props.theme.colors.accentGradient};
    margin-top: 0.9rem;
    border-radius: 1.8px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 0.99rem;
  color: ${props => props.theme.colors.secondaryText};
  margin-bottom: 2.7rem;
  line-height: 1.6;
`;

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 1.8rem;
`;

const InfoIcon = styled.div`
  width: 45px;
  height: 45px;
  background: ${props => props.theme.name === 'light'
    ? 'rgba(94, 23, 235, 0.1)'
    : 'rgba(94, 23, 235, 0.2)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.35rem;
  
  svg {
    color: ${props => props.theme.colors.accent};
    font-size: 1.35rem;
  }
`;

const InfoContent = styled.div``;

const InfoTitle = styled.h3`
  font-size: 1.08rem;
  margin-bottom: 0.45rem;
  color: ${props => props.theme.colors.text};
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.secondaryText};
`;

const InfoLink = styled.a`
  color: ${props => props.theme.colors.secondaryText};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover, &:focus {
    color: ${props => props.theme.colors.accent};
    text-decoration: underline;
  }
  
  &:focus {
    outline: 1.8px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }
`;

const ContactForm = styled.form`
  background: ${props => props.theme.colors.cardBackground};
  padding: 2.7rem;
  border-radius: 13.5px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  box-shadow: 0 13.5px 27px rgba(0, 0, 0, ${props => props.theme.name === 'light' ? '0.05' : '0.2'});
`;

const FormGroup = styled.div`
  margin-bottom: 1.35rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.45rem;
  font-size: 0.81rem;
  color: ${props => props.theme.colors.secondaryText};
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.9rem;
  background: ${props => props.theme.name === 'light'
    ? 'rgba(240, 240, 240, 0.5)'
    : 'rgba(15, 17, 20, 0.5)'};
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 7.2px;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 1.8px ${props => props.theme.name === 'light'
    ? 'rgba(94, 23, 235, 0.2)'
    : 'rgba(94, 23, 235, 0.3)'};
  }
  
  &:focus:not(:focus-visible) {
    outline: none;
  }
  
  &:focus-visible {
    outline: 1.8px solid ${props => props.theme.colors.accent};
    outline-offset: 0.9px;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.placeholderText || 'rgba(107, 114, 128, 0.8)'};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.9rem;
  background: ${props => props.theme.name === 'light'
    ? 'rgba(240, 240, 240, 0.5)'
    : 'rgba(15, 17, 20, 0.5)'};
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 7.2px;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  min-height: 135px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 1.8px ${props => props.theme.name === 'light'
    ? 'rgba(94, 23, 235, 0.2)'
    : 'rgba(94, 23, 235, 0.3)'};
  }
  
  &:focus:not(:focus-visible) {
    outline: none;
  }
  
  &:focus-visible {
    outline: 1.8px solid ${props => props.theme.colors.accent};
    outline-offset: 0.9px;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.placeholderText || 'rgba(107, 114, 128, 0.8)'};
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.accentGradient};
  color: white;
  padding: 0.9rem 1.8rem;
  border-radius: 45px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  width: 100%;
  cursor: pointer;
  border: none;
  
  &:hover {
    transform: translateY(-2.7px);
    box-shadow: 0 9px 18px rgba(94, 23, 235, 0.3);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2.7px ${props => props.theme.name === 'light'
    ? 'rgba(94, 23, 235, 0.4)'
    : 'rgba(94, 23, 235, 0.6)'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 0.9rem;
  padding: 0.72rem;
  border-radius: 7.2px;
  text-align: center;
  font-weight: 500;
  background-color: ${props => props.success ? 'rgba(52, 211, 153, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  color: ${props => props.success ? 'rgba(6, 95, 70, 1)' : 'rgba(185, 28, 28, 1)'};
  display: ${props => props.show ? 'block' : 'none'};
  
  /* Amélioration du contraste pour l'accessibilité */
  ${props => props.success ? `
    border: 1px solid rgba(6, 95, 70, 0.3);
  ` : `
    border: 1px solid rgba(185, 28, 28, 0.3);
  `}
`;

// Ajout d'attributs aria pour l'accessibilité
const PhoneIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const EmailIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const LocationIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({
    show: false,
    success: false,
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const statusRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ show: false, success: false, message: '' });

    const serviceID = 'service_gk6xkgt';  // ex: 'service_abc123'
    const templateID = 'template_gcvu19f'; // ex: 'template_abc123'
    const publicKey = '3MQ4M7xwveC3cwNfn';  // ex: 'abc123def456'

    emailjs.sendForm(serviceID, templateID, form.current, publicKey)
      .then((result) => {
        console.log('Email envoyé avec succès:', result.text);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setStatus({
          show: true,
          success: true,
          message: 'Votre message a été envoyé avec succès! Nous vous contacterons bientôt.'
        });
        setLoading(false);

        // Focus sur le message de statut pour les lecteurs d'écran
        if (statusRef.current) {
          statusRef.current.focus();
        }
      }, (error) => {
        console.error('Erreur lors de l\'envoi de l\'email:', error.text);
        setStatus({
          show: true,
          success: false,
          message: 'Erreur lors de l\'envoi du message. Veuillez réessayer plus tard.'
        });
        setLoading(false);

        // Focus sur le message de statut pour les lecteurs d'écran
        if (statusRef.current) {
          statusRef.current.focus();
        }
      });
  };

  return (
    <ContactSection id="contact" aria-labelledby="contact-title">
      <ContactContainer>
        <ContactInfo>
          <SectionTitle id="contact-title">Contactez-nous</SectionTitle>
          <SectionSubtitle>
            Vous avez un projet web en tête ? Parlez-nous-en ! Notre équipe se fera un plaisir de vous répondre dans les plus brefs délais.
          </SectionSubtitle>

          <InfoItem>
            <InfoIcon aria-hidden="true">
              <PhoneIcon />
            </InfoIcon>
            <InfoContent>
              <InfoTitle>Téléphone</InfoTitle>
              <InfoText>
                <InfoLink href="tel:+33766171996" aria-label="Appeler le +33 7 66 17 19 96">
                  +33 7 66 17 19 96
                </InfoLink>
              </InfoText>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIcon aria-hidden="true">
              <EmailIcon />
            </InfoIcon>
            <InfoContent>
              <InfoTitle>Email</InfoTitle>
              <InfoText>
                <InfoLink href="mailto:contact.rankeed@gmail.com" aria-label="Envoyer un email à contact.rankeed@gmail.com">
                  contact.rankeed@gmail.com
                </InfoLink>
              </InfoText>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIcon aria-hidden="true">
              <LocationIcon />
            </InfoIcon>
            <InfoContent>
              <InfoTitle>Adresse</InfoTitle>
              <InfoText>
                <InfoLink
                  href="https://maps.google.com/?q=La+Rochelle+17000+France"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Ouvrir l'adresse La Rochelle 17000, France dans Google Maps"
                >
                  La Rochelle 17000, France
                </InfoLink>
              </InfoText>
            </InfoContent>
          </InfoItem>
        </ContactInfo>

        <ContactForm
          ref={form}
          onSubmit={handleSubmit}
          aria-labelledby="form-title"
          noValidate
        >
          <h2 id="form-title" className="sr-only">Formulaire de contact</h2>

          <FormGroup>
            <FormLabel htmlFor="name">Nom</FormLabel>
            <FormInput
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Votre nom"
              aria-required="true"
              autoComplete="name"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="email">Email</FormLabel>
            <FormInput
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="votre-email@exemple.com"
              aria-required="true"
              autoComplete="email"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="subject">Sujet</FormLabel>
            <FormInput
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="Le sujet de votre message"
              aria-required="true"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="message">Message</FormLabel>
            <FormTextarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Détaillez votre projet ou votre demande..."
              aria-required="true"
            />
          </FormGroup>

          <SubmitButton
            type="submit"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Envoi en cours...' : 'Envoyer le message'}
          </SubmitButton>

          <StatusMessage
            show={status.show}
            success={status.success}
            ref={statusRef}
            tabIndex={status.show ? 0 : -1}
            role="alert"
            aria-live="assertive"
          >
            {status.message}
          </StatusMessage>
        </ContactForm>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact;